// Single source of truth for the documents collected on a student application.
// Both the GrowLearn (StudentDetail) and Admin (StudentTable) screens render from
// this list so the two stay in sync.
//
// `documents.document_type` is free-text in the DB, so adding a slot needs no migration.

export interface DocumentSlot {
  /** Stable key for form state. Unique across every slot. */
  id: string;
  /** Value written to documents.document_type. Repeatable slots share one type. */
  type: string;
  label: string;
  required: boolean;
  /** Older document_type values meaning the same thing, so applications created
   *  before a rename still match this slot in the upload/replace UI. */
  legacyTypes?: string[];
}

export interface DocumentSection {
  title: string;
  slots: DocumentSlot[];
}

/** How many "additional higher education" certificates a student may attach. */
export const MAX_ADDITIONAL_HIGHER_ED = 2;

export const ADDITIONAL_HIGHER_ED_TYPE = "Additional Higher Education Certificate";

/** How many qualification certificates a student may attach. */
export const MAX_QUALIFICATION_CERTIFICATES = 3;

export const QUALIFICATION_TYPE = "Qualification Certificate";

export const DOCUMENT_SECTIONS: DocumentSection[] = [
  {
    title: "Required Documents",
    slots: [
      {
        id: "identity",
        type: "Passport / National Identity Document",
        label: "Passport / National Identity Document",
        required: true,
        // Applications submitted before this section was split out used "ID Document".
        legacyTypes: ["ID Document"],
      },
      {
        id: "bachelor",
        type: "Bachelor's Degree Certificate",
        label: "Bachelor's Degree Certificate",
        required: true,
      },
      {
        id: "cv",
        type: "Curriculum Vitae (CV)",
        label: "Curriculum Vitae (CV)",
        required: true,
      },
    ],
  },
  {
    title: "Higher Education Certificates",
    slots: [
      ...Array.from({ length: MAX_QUALIFICATION_CERTIFICATES }, (_, i) => ({
        // The first slot keeps its original id so existing form state still lines up.
        id: i === 0 ? "qualification" : `qualification_${i + 1}`,
        type: QUALIFICATION_TYPE,
        label: `Qualification Certificate ${i + 1}`,
        required: false,
      })),
      {
        id: "master",
        type: "Master's Degree Certificate",
        label: "Master's Degree Certificate",
        required: false,
      },
    ],
  },
  {
    title: "Additional Higher Education Certificates",
    slots: Array.from({ length: MAX_ADDITIONAL_HIGHER_ED }, (_, i) => ({
      id: `additional_higher_ed_${i + 1}`,
      type: ADDITIONAL_HIGHER_ED_TYPE,
      label: `Additional Certificate ${i + 1}`,
      required: false,
    })),
  },
];

export const DOCUMENT_SLOTS: DocumentSlot[] = DOCUMENT_SECTIONS.flatMap((s) => s.slots);

export const REQUIRED_DOCUMENT_SLOTS: DocumentSlot[] = DOCUMENT_SLOTS.filter((s) => s.required);

/** Choices for the standalone "Add Document" picker (repeatable types listed once). */
export const DOCUMENT_TYPE_OPTIONS: string[] = [
  ...Array.from(new Set(DOCUMENT_SLOTS.map((s) => s.type))),
  "Other",
];

/** True when a stored row belongs in this slot, tolerating renamed legacy types. */
export const docMatchesSlot = (documentType: string, slot: DocumentSlot): boolean =>
  documentType === slot.type || (slot.legacyTypes?.includes(documentType) ?? false);

interface DocLike {
  id: string;
  document_type: string;
}

/**
 * Assign stored documents to slots, one document per slot. Repeatable slots that
 * share a type are filled in order, so two "Additional ..." rows land in
 * additional_higher_ed_1 and _2 rather than both matching the first slot.
 */
export const mapDocsToSlots = <T extends DocLike>(docs: T[]): Record<string, T> => {
  const claimed = new Set<string>();
  const bySlot: Record<string, T> = {};
  for (const slot of DOCUMENT_SLOTS) {
    const match = docs.find((d) => !claimed.has(d.id) && docMatchesSlot(d.document_type, slot));
    if (match) {
      claimed.add(match.id);
      bySlot[slot.id] = match;
    }
  }
  return bySlot;
};
